import { Link } from "react-router-dom";
import { contactEmail, contactPhone, contactPhoneRaw } from "@/components/pivotbox/siteContent";

/**
 * SiteFooter — global footer used on every public page.
 * Three columns + a green SHARE row + copyright.
 */
export default function SiteFooter() {
  const shareTitle = "Pivotbox — Clarity before commitment.";
  const shareUrl = typeof window !== "undefined" ? window.location.href : "https://thepivotbox.com/";
  const enc = (s) => encodeURIComponent(s);
  const waHref = `https://api.whatsapp.com/send?text=${enc(shareTitle)}%20${enc(shareUrl)}`;
  const liHref = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(shareUrl)}`;
  const igHref = "https://www.instagram.com/thepivotbox";

  return (
    <footer className="site-footer" id="site-footer" data-testid="site-footer">
      <div className="pivotbox-container site-footer__inner">
        <div className="site-footer__grid">
          {/* Section A — Policy Pages */}
          <div className="site-footer__col" data-testid="footer-policies">
            <h3 className="site-footer__heading">Policy Pages</h3>
            <ul className="site-footer__list">
              <li><Link to="/disclaimer" data-testid="footer-disclaimer">Disclaimer Policy</Link></li>
              <li><Link to="/privacy-policy" data-testid="footer-privacy">Privacy Policy</Link></li>
              <li><Link to="/terms-of-use" data-testid="footer-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/refund-policy" data-testid="footer-refund">Refund Policy</Link></li>
            </ul>
          </div>

          {/* Section B — Contact us */}
          <div className="site-footer__col" data-testid="footer-contact">
            <h3 className="site-footer__heading">Contact Us</h3>
            <ul className="site-footer__list">
              <li>
                <span className="site-footer__label">Email</span>
                <a href={`mailto:${contactEmail}`} data-testid="footer-contact-email">{contactEmail}</a>
              </li>
              <li>
                <span className="site-footer__label">Phone</span>
                <a href={`tel:${contactPhoneRaw}`} data-testid="footer-contact-phone">{contactPhone}</a>
              </li>
            </ul>
          </div>

          {/* Section C — Quick Links */}
          <div className="site-footer__col" data-testid="footer-quicklinks">
            <h3 className="site-footer__heading">Quick Links</h3>
            <ul className="site-footer__list">
              <li><Link to="/" data-testid="footer-qlink-home">Home</Link></li>
              <li><Link to="/find-out" data-testid="footer-qlink-find-out">Pivotbox Full Diagnostic</Link></li>
              <li><Link to="/s0diagnostic" data-testid="footer-qlink-s0">S0 &mdash; Founder Signal test</Link></li>
              <li><Link to="/s1diagnostic" data-testid="footer-qlink-s1">S1 &mdash; Founder Reality check</Link></li>
              <li><Link to="/S21diagnostic" data-testid="footer-qlink-s21">S2.1 &mdash; Founder Archetype assessment</Link></li>
              <li><Link to="/S22diagnostic" data-testid="footer-qlink-s22">S2.2 &mdash; Founder Durability assessment</Link></li>
              <li><Link to="/S23diagnostic" data-testid="footer-qlink-s23">S2.3 &mdash; Founder Capability &amp; Execution profile</Link></li>
              <li><Link to="/strategyblueprint" data-testid="footer-qlink-s3">S3 &mdash; Founder Strategy Blueprint</Link></li>
              <li><Link to="/founderoperatingsystem" data-testid="footer-qlink-s4">S4 &mdash; Founder Operating System</Link></li>
            </ul>
          </div>
        </div>

        {/* SHARE row */}
        <div className="site-footer__share" data-testid="footer-share">
          <span className="site-footer__share-label">SHARE</span>
          <a
            href={waHref}
            target="_blank"
            rel="noopener noreferrer"
            className="site-footer__share-btn"
            aria-label="Share on WhatsApp"
            data-testid="footer-share-whatsapp"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M19.05 4.91A10 10 0 0 0 4.6 17.78L3 22l4.35-1.13a10 10 0 0 0 4.66 1.16h.01A10 10 0 0 0 22 12.05a9.94 9.94 0 0 0-2.95-7.14ZM12.02 20.2h-.01a8.16 8.16 0 0 1-4.16-1.14l-.3-.18-2.58.67.69-2.51-.2-.32a8.18 8.18 0 1 1 6.56 3.48Zm4.7-6.13c-.26-.13-1.52-.75-1.76-.84-.24-.09-.41-.13-.59.13-.17.26-.67.84-.82 1.01-.15.17-.3.2-.56.07-.26-.13-1.09-.4-2.07-1.28a7.79 7.79 0 0 1-1.43-1.78c-.15-.26-.02-.4.11-.53.11-.11.26-.3.39-.45.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.07-.13-.59-1.42-.81-1.95-.21-.51-.43-.44-.59-.45h-.5c-.17 0-.45.07-.69.32-.24.26-.91.89-.91 2.18 0 1.28.93 2.52 1.06 2.7.13.17 1.83 2.79 4.43 3.91.62.27 1.1.43 1.48.55.62.2 1.18.17 1.63.1.5-.07 1.52-.62 1.74-1.22.21-.6.21-1.12.15-1.22-.06-.11-.24-.17-.5-.3Z"/></svg>
          </a>
          <a
            href={liHref}
            target="_blank"
            rel="noopener noreferrer"
            className="site-footer__share-btn"
            aria-label="Share on LinkedIn"
            data-testid="footer-share-linkedin"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.05-1.86-3.05-1.86 0-2.14 1.45-2.14 2.95v5.67H9.35V9h3.41v1.56h.05a3.74 3.74 0 0 1 3.37-1.85c3.61 0 4.27 2.38 4.27 5.47v6.27ZM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12ZM7.12 20.45H3.56V9h3.56v11.45Z"/></svg>
          </a>
          <a
            href={igHref}
            target="_blank"
            rel="noopener noreferrer"
            className="site-footer__share-btn"
            aria-label="Follow on Instagram"
            data-testid="footer-share-instagram"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="5" ry="5"/><path d="M16 11.4a4 4 0 1 1-4.6-4.6 4 4 0 0 1 4.6 4.6Z"/><line x1="17.5" y1="6.5" x2="17.5" y2="6.5"/></svg>
          </a>
        </div>

        <div className="site-footer__copyright" data-testid="footer-copyright">
          © {new Date().getFullYear()} The Pivotbox — a brand of Sargam International Trading Company. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
